import jsonToFormData from "utilities/jsonToFormData"
import { usePostExportReport } from "api/hooks/reports"
import useAppState from "context/hooks/useAppState"
import { EXPORT_GRID_TASKS } from "constants/tasks"

// assets
import imageThumbnail from "assets/images/document-thumbnails/image-thumbnail.jpg"
import musicThumbnail from "assets/images/document-thumbnails/music-thumbnail.jpg"
import rtfThumbnail from "assets/images/document-thumbnails/rtf-thumbnail.jpg"
import wordDocThumbnail from "assets/images/document-thumbnails/word-doc-thumbnail.jpg"
import xmlThumbnail from "assets/images/document-thumbnails/xml-thumbnail.jpg"
import genericThumbnail from "assets/images/document-thumbnails/doc-generic-thumbnail.jpg"

// antd validators
import { fileLinkValidator, linkFileValidator, validateUniqueFilename } from "utilities/antFormUtils"

// PR NOTES: please move these utilities to  to fileUtils in /utilities
// Update all files importing these functions and delete this file after

const isValidUrl = (str) => {
  var pattern = new RegExp("(http|https)://[a-zA-Z0-9./?=_%:-]*", "i")
  return !!pattern.test(str)
}

/**
 * if not PDF, find file type
 * ...returns "doc", "docx"...
 *
 * Could also check for fileName.type,
 * ...eg, "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
 */
const getFileType = (val) => {
  let fileName = val
  if (val) fileName = val.toLowerCase()
  if (fileName) {
    if (fileName.includes(".pdf")) return "pdf"
    else if (fileName.includes(".mp3")) return "mp3"
    else if (fileName.includes(".mp4")) return "mp4"
    else if (fileName.includes(".png")) return "png"
    else if (fileName.includes(".rtf")) return "rtf"
    else if (fileName.includes(".doc")) return "doc"
    else if (fileName.includes(".docx")) return "docx"
    else if (fileName.includes(".xml")) return "xml"
  }
  return null
}

const getFileThumbnail = (fileName) => {
  const getImgEl = (thumbnailImg) => {
    return (
      <img
        src={thumbnailImg}
        className="thumbnail-card"
        alt="file icon"
      />
    )
  }
  const fileType = getFileType(fileName)

  if (fileType === "jpg" || fileType === "jpeg" || fileType === "png") {
    return getImgEl(imageThumbnail)
  } else if (fileType === "mp3" || fileType === "mp4") {
    return getImgEl(musicThumbnail)
  } else if (fileType === "rtf") {
    return getImgEl(rtfThumbnail)
  } else if (fileType === "doc" || fileType === "docx") {
    return getImgEl(wordDocThumbnail)
  } else if (fileType === "xml") {
    return getImgEl(xmlThumbnail)
  } else {
    // TODO: return other popular file types or generic file type thumbnail
    return getImgEl(genericThumbnail)
  }
}

// Error validation when a user fills out a form for a document, which requires either a file to be uploaded or a link to be added
const getFileLinkError = ({ documentFile, documentLink }) => {
  if (!documentFile && !documentLink) {
    return "Either upload a file or add a file link."
  } else if (documentFile && documentLink) {
    return "Only upload a file or add file link, not both."
  } else if (documentLink && !isValidUrl(documentLink)) {
    return "Enter a valid URL starting with https://."
  } else {
    return null
  }
}

const getDocFileName = (documentFile) => {
  if (!documentFile) return ""
  const slashParts = documentFile.split("/")
  // remove file signature param:
  return slashParts[slashParts.length - 1]?.split("?")?.[0]
}

// TODO: review and see if required
const getPutData = (file) => {
  let data = {
    // required fields
    name: file?.name || "",
    document_type_id: file?.document_type_id || "",
  }

  // add to authoringstudy_db_ids
  if (
    file?.authoringstudies_w_checklists &&
    Array.isArray(file.authoringstudies_w_checklists) &&
    file.authoringstudies_w_checklists.length > 0
  ) {
    data.authoringstudy_db_ids = file?.authoringstudies_w_checklists?.map((study) => study.id)
  }

  if (file?.requests && Array.isArray(file.requests) && file.requests.length > 0) {
    data.request_db_ids = file?.requests?.map((req) => req.id)
  }

  // TODO: add status to api // status: file?.status || '',
  // add fields if values on server
  const fields = [
    "document_file",
    "document_link",
    "version_number",
    "document_date",
    "extra_data",
    "state", // "status",
    // TODO: add checklist_db_ids
  ]
  fields.forEach((field) => {
    if (file?.[field]) {
      if (Array.isArray(file[field])) {
        if (file[field].length > 0) {
          data[field] = file[field]
        }
      } else if (typeof file[field] === "object") {
        if (Object.keys(file[field]).length > 0) {
          data[field] = file[field]
        }
      } else {
        data[field] = file[field]
      }
    }
  })

  if (data.version_number === "None") {
    data.version_number = ""
  }

  return data
}

// Used for documents PUT and POST
const getSavePayload = (data) => {
  const payload = { ...data }

  for (const key in data) {
    if (data[key] === null || data[key] === "None") {
      payload[key] = ""
    } else if (Array.isArray(data[key])) {
      payload[key] = JSON.stringify([...data[key]])
    } else if (
      typeof data[key] === "object" &&
      !(typeof data[key]?.name === "string") // File type
    ) {
      payload[key] = JSON.stringify({ ...data[key] })
    } else if (data[key] === undefined) {
      delete payload[key]
    }
  }

  return payload
}

const getPostRequest = (data) => {
  const body = getSavePayload({ ...data })
  return {
    body: jsonToFormData(body),
    headers: {
      "Content-Type": "application/json",
    },
  }
}

const REPORT_URL = "reports/files"

const useFilesExport = () => {
  const { setTaskType, setPollingTaskId } = useAppState()
  const postExportReport = usePostExportReport(REPORT_URL)

  const handleExport = (gridRef) => {
    let payload = {
      report_url: REPORT_URL,
      preset_data: {
        column_state: gridRef.current.api.getColumnState().filter((col) => {
          if (col.hide === true) return false
          return true
        }),
        filter_state: gridRef.current.api.getFilterModel(),
        sort_state: getSortModel(gridRef),
      },
      report_type: "study",
    }

    postExportReport(
      {
        body: payload,
      },
      {
        onSuccess: (resp) => {
          setPollingTaskId(resp.data.task_id)
          setTaskType(EXPORT_GRID_TASKS.filesGrid)
        },
      }
    )
  }

  const getSortModel = (gridRef) => {
    const value = gridRef.current.api.getColumnState().find((s) => s.sort != null)
    if (value) {
      return [value]
    } else {
      return []
    }
  }

  return {
    handleExport,
  }
}

export {
  isValidUrl,
  getFileLinkError,
  getDocFileName,
  getPutData,
  getSavePayload,
  getPostRequest,
  useFilesExport,
  getFileType,
  getFileThumbnail,

  // antd validators
  fileLinkValidator,
  linkFileValidator,
  validateUniqueFilename,
}
